import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from '../i18n/translations';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginProps {
  onAuth: (user: TelegramUser) => void;
  botUsername?: string;
  buttonSize?: 'large' | 'medium' | 'small';
  cornerRadius?: number;
  requestAccess?: boolean;
}

// Global counter to ensure unique IDs
let widgetCounter = 0;

export function TelegramLogin({ 
  onAuth, 
  botUsername = 'ZiaWellNavBot',
  buttonSize = 'large',
  cornerRadius = 10,
  requestAccess = true
}: TelegramLoginProps) {
  const { t } = useTranslation();
  const scriptContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>(`telegram-login-${++widgetCounter}`);
  const onAuthRef = useRef(onAuth);
  const scriptLoadedRef = useRef(false);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  
  // Keep onAuth ref updated
  onAuthRef.current = onAuth;
  
  // Stable callback that uses ref
  const handleAuth = useCallback((user: TelegramUser) => {
    console.log('Telegram auth callback received:', user);
    onAuthRef.current(user);
  }, []);
  
  useEffect(() => {
    // Don't re-run if script already loaded
    if (scriptLoadedRef.current) return;
    
    const containerId = widgetIdRef.current;
    const callbackName = `onTelegramAuth_${containerId.replace(/-/g, '_')}`;
    
    // Create unique callback function on window
    (window as any)[callbackName] = (user: TelegramUser) => {
      console.log(`${callbackName} fired with:`, user);
      handleAuth(user);
    };

    // Load Telegram Login Widget script
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', buttonSize);
    script.setAttribute('data-radius', cornerRadius.toString());
    script.setAttribute('data-onauth', `${callbackName}(user)`);
    script.setAttribute('data-request-access', requestAccess ? 'write' : '');
    script.async = true;
    
    script.onload = () => {
      console.log('Telegram widget script loaded');
      scriptLoadedRef.current = true;
      setStatus('loaded');
    };
    
    script.onerror = () => {
      console.error('Failed to load Telegram widget');
      setStatus('error');
    };

    const container = scriptContainerRef.current;
    if (container) {
      container.appendChild(script);
    }

    return () => {
      // Cleanup callback after delay
      setTimeout(() => {
        delete (window as any)[callbackName];
      }, 2000);
    };
  }, [botUsername, buttonSize, cornerRadius, requestAccess, handleAuth]);

  return (
    <div 
      className="flex justify-center items-center flex-col" 
      style={{ minHeight: '40px' }}
    >
      {/* Script container - separate from React-managed content */}
      <div 
        ref={scriptContainerRef}
        id={widgetIdRef.current}
        style={{ minHeight: status === 'loading' ? 0 : 'auto' }}
      />
      
      {/* Loading state - managed by React */}
      {status === 'loading' && (
        <span style={{ color: '#aaa', fontSize: '14px' }}>{t('telegramLoginLoading')}</span>
      )}
      
      {/* Error fallback - managed by React */}
      {status === 'error' && (
        <a 
          href={`https://t.me/${botUsername}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            textDecoration: 'none',
            padding: '10px 20px',
            backgroundColor: '#0088cc',
            color: 'white',
            borderRadius: '8px',
            display: 'inline-block'
          }}
        >
          {t('telegramLoginOpenBot', botUsername)}
        </a>
      )}
    </div>
  );
}
